import ProductAPI from 'api/product.api'
import { makeAutoObservable, runInAction, action, toJS } from 'mobx'
import { debounce, keyBy, groupBy, get, cloneDeep, omit, set, concat, unionBy, isEmpty, isUndefined } from '@dt/fe-utils/dist/lodash'
import { ddayjs } from '@dt/fe-utils'
import { allMenuMobx, restaurantCenter, restaurantConfig, RESTAURANT_EVENT, productCenter, productMobx } from '..'

const setProductSale = (params: any) => {
  ProductAPI.setProductSale(params).catch((err: any) => {
    if (err && err.error) {
      restaurantCenter.emit(RESTAURANT_EVENT.PRODUCT_SALE_SYNC_STATUS, false)
    }
  })
}

const saveSaleList = debounce((params: any) => {
  setProductSale(params)
}, 3003)

const calcScaleCnt = (originData: any) => {
  const map: { [productNoScaleNo: string]: ICalcScale } = {}
  const keyByData = keyBy(originData, (v: IProductSaleHandle) => `${v.productNo}_${v.scaleNo || 0}`)
  for (let key in keyByData) {
    map[key] = {
      notForSaleValue: keyByData[key].notForSaleValue,
      event: keyByData[key].event,
      productType: keyByData[key].productType,
      cnt: keyByData[key].cnt,
      totalCnt: keyByData[key].totalCnt,
      restoreType: keyByData[key].restoreType,
      notForSaleReason: keyByData[key].notForSaleReason
    }
  }
  return map
}

const calcProductCnt = (originData: any) => {
  const map: { [productNo: string]: { cnt: number; event: string; totalCnt: number; type?: 'today' | 'long' | 'everyday' }} = {}
  const groupByData = groupBy(originData, 'productNo')
  for (let key in groupByData) {
    let cnt = 0
    let totalCnt = 0
    if (groupByData[key].length === allMenuMobx.noToViewProductMap[key]?.scales.length || groupByData[key][0].scaleNo === '-1') {
      // 只有设置到餐品级的餐品统计限售数量
      groupByData[key].map((v: any) => {
        cnt += v.cnt
        totalCnt += v.totalCnt
      })
      map[key] = {
        cnt: Number(cnt.toFixed()),
        totalCnt: Number(totalCnt.toFixed()),
        event: get(groupByData[key], '0.event') as any
      }
    }

    if (groupByData[key][0].scaleNo === '-1') {
      map[key] = { ...map[key], type: groupByData[key][0].restoreType }
    }
  }
  return map
}
class ProductSale {
  constructor () {
    makeAutoObservable(this, {
      selectCategory: action.bound
    })
    restaurantCenter.on(RESTAURANT_EVENT.PRODUCT_SALE_CHANGED, async (data) => {
      await this.init()
      this.setHandleSort(data)
    })
    restaurantCenter.on(RESTAURANT_EVENT.MENU_START, () => {
      this.init()
    })
  }
  /** 当前售卖餐品状态列表原始数据 */
  saleList: IProductSaleHandle[] = [];
  /** 售卖餐品列表操作中数据 */
  handleList: IProductSaleHandle[] = [];
  /** 因为供应链物料不足而需要估清的餐品ID（当前暂时到餐品级） */
  noMaterialProductIds: string[] = []
  /** 售卖中心分类选中的no */
  selectCategoryNo = ''

  /** 云端规格级可售状态 */
  get scaleNoToCntMap (): { [productNoScaleNo: string]: ICalcScale} {
    return calcScaleCnt(this.saleList)
  }

  /** 云端餐品级可售状态 */
  get productNoToCntMap (): { [productNo: string]: { cnt: number; totalCnt: number } } {
    return calcProductCnt(this.saleList)
  }

  /** 云端菜单餐品级可售状态 专供外部数据调用 */
  get menuProductNoToCntMap (): { [productNo: string]: { cnt: number; totalCnt: number} } {
    const map: { [productNo: string]: { cnt: number; totalCnt: number } } = {}
    const products = keyBy(productMobx.products, 'no')
    if (this.saleList.length) {
      const groupByData = groupBy(this.saleList, 'productNo')
      for (let key in groupByData) {
        let cnt = 0
        let totalCnt = 0
        if ((products[key] && products[key].scaleGroup.scales.every(scale => groupByData[key].find(v => v.scaleNo === scale.no))) || groupByData[key][0].scaleNo === '-1') {
          // 只有设置到餐品级的餐品统计限售数量
          if (groupByData[key]?.some(item => item.event === 'NotForSale' && item.notForSaleReason === 'comboChildPause')) {
            cnt = 0
            totalCnt = 0
          } else if (groupByData[key][0].scaleNo === '-1') {
            cnt += groupByData[key][0].cnt || 0
            totalCnt += groupByData[key][0].totalCnt || 0
          } else {
            groupByData[key].map((v: any) => {
              const product = products[key]
              if (product?.scaleGroup.scales.find(scale => scale.no === v.scaleNo)) {
                cnt += v.cnt
                totalCnt += v.totalCnt
              }
            })
          }
          map[key] = {
            cnt: Number(cnt.toFixed()),
            totalCnt: Number(totalCnt.toFixed())
          }
        }
      }
      const modifySale = this.saleList.filter(item => item.event === 'NotForSale')
      if (modifySale?.length) {
        modifySale.forEach(item => {
          if (products[item.productNo] && isEmpty(item.scaleNo)) {
            map[item.productNo] = {
              cnt: Number(item.cnt?.toFixed()),
              totalCnt: Number(item.totalCnt?.toFixed())
            }
          }
        })
      }
    }
    return map
  }

  /** 本地规格级可售状态 */
  get localScaleNoToCntMap (): { [productNoScaleNo: string]: ICalcScale } {
    return calcScaleCnt(this.avaliableList)
  }

  /** 餐品级合计规格项的可售状态 */
  get productNoToScaleCntMap (): { [productNo: string]: { cnt: number; event: string; totalCnt: number; type?: 'today' | 'long' | 'everyday' } } {
    const originData = this.saleList
    const map: { [productNo: string]: { cnt: number; event: string; totalCnt: number; type?: 'today' | 'long' | 'everyday' }} = {}
    const groupByData = groupBy(originData, 'productNo')
    for (let key in groupByData) {
      let cnt = 0
      let totalCnt = 0
      // 只有设置到餐品级的餐品统计限售数量
      groupByData[key].map((v: any) => {
        cnt += v.cnt
        totalCnt += v.totalCnt
      })
      map[key] = {
        cnt: Number(cnt.toFixed()),
        totalCnt: Number(totalCnt.toFixed()),
        event: get(groupByData[key], '0.event') as any
      }

      if (groupByData[key][0].scaleNo === '-1') {
        map[key] = { ...map[key], type: groupByData[key][0].restoreType }
      }
    }
    return map
  }

  /** 本地餐品级可售状态 */
  get localProductNoToCntMap (): { [productNo: string]: { cnt: number; event: string; totalCnt: number; type?: 'today' | 'long' | 'everyday' } } {
    return calcProductCnt(this.avaliableList)
  }

  /** 限售餐品统计状态 */
  get groupBySaleProduct (): { [productNo: string]: IProductSaleHandle[] } {
    const groupByData = groupBy(this.avaliableList, 'productNo')
    return groupByData
  }

  // 是否处于限售停售状态
  get isInLimit () {
    let limit = false
    Object.keys(this.scaleNoToCntMap).map((key: string) => {
      if (this.scaleNoToCntMap[key]?.cnt > 0) {
        limit = true
      }
    })
    return limit
  }

  /** 获取供应链无物料餐品列表 */
  get noMaterialProducts () {
    return this.noMaterialProductIds
      .filter(productId => allMenuMobx.noToViewProductMap[productId])
      .map(productId => allMenuMobx.noToViewProductMap[productId])
  }
  get notSingleInComboPro () {
    const notSingleInComboPro: any[] = []
    this.noMaterialProducts.forEach((item: any) => {
      if (!item.isNotSingleInCombo) {
        notSingleInComboPro.push(item)
      }
    })
    return notSingleInComboPro
  }
  get categories () {
    let categories = allMenuMobx.categories
    if (this.noMaterialProductIds.length) {
      categories = [{
        no: '-1',
        name: '',
        products: this.noMaterialProducts
      }, ...categories]
    }
    if (allMenuMobx.notSingleInComboPro.length) {
      categories = [
        ...categories,
        {
          no: '99999',
          name: i18n.t('cashier.comboProduct'),
          products: allMenuMobx.notSingleInComboPro
        }
      ]
    }
    return categories
  }

  get products () {
    if (this.selectCategoryNo === '-1') {
      return this.noMaterialProducts
    } else if (this.selectCategoryNo === '99999') {
      return allMenuMobx.notSingleInComboPro
    } else {
      return allMenuMobx.products
    }
  }

  @action
  selectCategory (category?: any) {
    this.selectCategoryNo = (typeof category === 'string' || typeof category === 'undefined') ? category : category.no
    if (this.selectCategoryNo !== '-1') {
      allMenuMobx.selectCategory(category)
    }
  }
  async init (isRecord?: boolean, isNoMaterial?: boolean, brandId: number = restaurantConfig.brandId, storeId: number = restaurantConfig.storeId) {
    if (isNoMaterial) {
      this.initNoMaterialProduct()
    }
    // let storeHandleSort = []
    // try {
    //   storeHandleSort = await nativeStore.getItem(PRODUCT_SALE_OUT_HANDLE_SORT)
    // } catch (e) {
    //   console.error('nativeStore.getItem(PRODUCT_SALE_OUT_HANDLE_SORT) error', e)
    // }
    // if (storeHandleSort && storeHandleSort.length > 0) {
    //   this.handleSort = storeHandleSort
    // }
    const isToday = !productCenter.appointmentDate
    return ProductAPI.getProductSale(brandId, storeId, isToday).then((saleDataList: IProductSaleHandle[]) => {
      runInAction(() => {
        this.saleList = saleDataList
        this.handleList = []
        if (isRecord || !this.handleSort?.length) {
          this.handleSort = Object.keys(groupBy(saleDataList, 'productNo'))
        }
      })
    }, (err: any) => {
      console.error('get product sale api error', err)
    })
  }
  // empty () {
  //   this.saleList = []
  //   this.handleList = []
  //   nativeStore.setItem(PRODUCT_SALE_OUT_HANDLE_SORT, this.handleSort).catch((err) => {
  //     console.error('nativeStore.setItem(PRODUCT_SALE_OUT_HANDLE_SORT', err)
  //   })
  //   this.handleSort = []
  // }
  handleModifySort: IModifySort= {}
  setModifySort (sort: IModifySort) {
    this.handleModifySort = sort
  }
  /** 限售餐品下单减库存后，将餐品id排序提前 */
  setHandleSort (data: any) {
    if (data?.type === 'productInventory.reduse') {
      const inventorGroup = keyBy(data?.productInventoryList, 'productNo') || {}
      const reduceInventor = this.viewAvaliableSaleList.filter(item => inventorGroup[item.productNo] && (!item.isInventorHasScale || item?.scales?.some(s => s.no === inventorGroup[item.productNo]?.scaleNo)))
      if (reduceInventor.length) {
        const frontSort = keyBy(reduceInventor, (i) => i.productNo)
        this.handleSort = Object.keys(frontSort).concat(this.handleSort.filter(i => !frontSort[i]))
      }
    }
  }

  soldOrRestoreProductIds (productIds: string[], restore: boolean) {
    const dataList: IProductSaleHandle[] = productIds.filter(productId => allMenuMobx.noToViewProductMap[productId]).map((productId: string) => {
      const product = allMenuMobx.noToViewProductMap[productId]
      return {
        productNo: product.no,
        productName: product.name,
        scaleNo: '-1',
        scaleName: product.scales[0].name,
        event: restore ? 'Close' : 'Pause',
        cnt: restore ? undefined : 0,
        totalCnt: restore ? undefined : 0,
        restoreType: 'today'
      }
    })
    return this.changeDataList(dataList)
  }
  /** 恢复/停售单个餐品或者全部恢复 */
  resetPauseSale (event: 'Close' | 'Pause' | 'Limit', productNo?: string, scaleNo?: string) {
    let params: IProductSaleHandle[] = []
    if (scaleNo && scaleNo !== '-1' && productNo) {
      // 餐品的no索引 -> map计算
      const viewProduct = allMenuMobx.noToViewProductMap[productNo]
      // 获取本地规格售卖情况
      const localScaleNoToCntMap = this.localScaleNoToCntMap
      params = viewProduct.scales.map(scale => {
        const status = localScaleNoToCntMap[productNo + '_' + scale.no]
        return {
          productNo: productNo,
          scaleNo: scale.no,
          event: scaleNo === scale.no ? event : !status ? 'Close' : status.cnt === 0 ? 'Pause' : 'Limit',
          cnt: ((scaleNo === scale.no && event === 'Close') || !status) ? undefined : status.cnt,
          totalCnt: ((scaleNo === scale.no && event === 'Close') || !status) ? undefined : status.cnt,
          restoreType: status?.restoreType || (localStorage.getItem('ls.restoreType') as 'today' | 'long') || 'today'
        }
      })
    } else {
      params = productNo ? [{
        productNo: productNo,
        scaleNo: scaleNo || '-1',
        event: event,
        cnt: event === 'Close' ? undefined : 0,
        totalCnt: event === 'Close' ? undefined : 0,
        restoreType: (localStorage.getItem('ls.restoreType') as 'today' | 'long' | 'everyday' | undefined) || 'today'
      }] : this.avaliableList.filter(item => item.productType !== 'modifier' && item.event !== 'NotForSale').map(v => {
        return {
          productNo: v.productNo,
          scaleNo: '-1',
          event: 'Close',
          restoreType: undefined
        }
      })
    }
    this.changeDataList(params)
  }

  /** 获取无物料餐品 */
  async initNoMaterialProduct () {
    return ProductAPI.getNoMaterialProductByStore().then((res: any) => {
      runInAction(() => {
        this.noMaterialProductIds = res?.result?.content?.map((sale: any) => sale.productNo) || []
      })
    })
  }

  /** 新数据开启 */
  handleSort: string[] = [];

  /** 可用的排序 */
  get avaliableSort () {
    const sorts = this.handleSort.filter(no => !!allMenuMobx.noToViewProductMap[no] && this.avaliableList?.find(v => v.productNo === no && v.event !== 'NotForSale'))
    const lackList = Object.keys(groupBy((this.avaliableList || [])?.filter(v => (v.event !== 'NotForSale' && v.productType !== 'modifier')), 'productNo')).filter(no => !sorts.includes(no))
    return sorts.concat(lackList)
  }

  /** 可用的本地数据 */
  get avaliableList (): IProductSaleHandle[] {
    const handleListMap = groupBy(this.handleList, 'productNo')
    const newList = this.saleList.filter(sale => {
      const hanleData = handleListMap[sale.productNo]
      // todo
      if (hanleData) {
        // 过滤scaleNo === '-1' 规格沽清
        if (sale.scaleNo === '-1') {
          return false
        } else if (hanleData.find(v => v.scaleNo === sale.scaleNo)) {
          return false
        } else if (hanleData.find(v => v.scaleNo === '-1')) {
          return false
        }
      }
      return true
    })
    const collectList = [...this.handleList, ...newList]
    const legalList = collectList.filter(v => !!(allMenuMobx.noToViewProductMap[v.productNo] || v.productType === 'modifier') && (v.cnt || v.cnt === 0))
    return legalList
  }
  /** 因为modify而估清的product
   * 去除非子商品 comboChildPause
   */
  get viewModifyNotForSaleProduct () {
    return unionBy((this.avaliableList || []).filter(item => item.event === 'NotForSale' && item.notForSaleReason !== 'comboChildPause').map((item) => this.mapViewSaleOutProduct(item.productNo)), (data) => data.productNo + (data.scales || []).map(i => i.no).join('_'))
  }
  /** 因为子商品估清的product
   * 判断是否子商品用comboChildPause判断
  */
  get viewNotForSaleChidrenProduct () {
    return unionBy((this.avaliableList || []).filter(item => item.event === 'NotForSale' && item.notForSaleReason === 'comboChildPause').map((item) => this.mapViewSaleOutProduct(item.productNo)), (data) => data.productNo + (data.scales || []).map(i => i.no).join('_'))
  }
  /** 限售数据转换 */
  get viewAvaliableSaleList () {
    return this.avaliableSort.map((key) => this.mapViewSaleOutProduct(key))
  }
  /**  根据product key,映射相应的估清列表 */
  mapViewSaleOutProduct (key: string) {
    const viewProduct = allMenuMobx.noToViewProductMap[key]
    const saleByProductNo = groupBy((this?.avaliableList || []), 'productNo')
    const localScaleNoToCntMap = this.localScaleNoToCntMap
    const localProductNoToCntMap = this.localProductNoToCntMap
    const newScales = saleByProductNo[key]?.map(v => {
      return {
        totalCnt: v.totalCnt || 0,
        no: v.scaleNo,
        cnt: v.cnt,
        event: v.event,
        name: allMenuMobx.noToViewProductScaleMap[key + '_' + v.scaleNo]?.scaleName || '',
        type: localScaleNoToCntMap[key + '_' + v.scaleNo]?.restoreType || 'today'
      }
    }) || []
    return {
      productNo: key,
      name: viewProduct?.name,
      scales: newScales,
      isInventorHasScale: newScales?.length > 1 && (get(newScales, '0.no') as any)?.length > 2,
      isMulity: viewProduct?.scales?.length > 1,
      type: localProductNoToCntMap[key]?.type || 'today'
    }
  }
  converModify (modify: IModifier, type: string, cnt: number | undefined, restoreType: 'today' | 'long' | 'everyday') {
    // const newHandleList: IProductSaleHandle[] = cloneDeep(modifier.map(modify => ({
    //   productName: modify.name,
    //   productNo: modify.no,
    //   scaleNo: '0',
    //   cnt: type === 'saleOut' ? cnt : undefined,
    //   event: type === 'saleOut' ? (cnt ? 'Limit' : 'Pause') : 'Close',
    //   totalCnt: type === 'saleOut' ? cnt : undefined,
    //   restoreType: (type === 'saleOut' && restoreType === 'today') ? 'today' : null,
    //   productType: 'modifier',
    //   startTime: (type === 'saleOut' && restoreType === 'today') ? `${ddayjs().add(1, 'day').format('YYYY-MM-DD')} 00:00:00` : null
    // })))
    let params: any = {
      productName: modify.name,
      productNo: modify.no,
      scaleNo: '0',
      cnt: type === 'saleOut' || type === 'batchSaleOut' ? cnt : undefined,
      event: type === 'saleOut' || type === 'batchSaleOut' ? (cnt ? 'Limit' : 'Pause') : 'Close',
      totalCnt: type === 'saleOut' || type === 'batchSaleOut' ? cnt : undefined,
      restoreType: null,
      productType: 'modifier',
      startTime: null
    }
    if (restoreType === 'today' && (type === 'saleOut' || type === 'batchSaleOut')) {
      params = {
        ...params,
        restoreType: 'today',
        startTime: `${ddayjs().add(1, 'day').format('YYYY-MM-DD')} 00:00:00`
      }
    }
    if (restoreType === 'everyday' && (type === 'batchSaleOut' || type === 'saleOut')) {
      params = {
        ...params,
        cnt: isUndefined(cnt) ? undefined : cnt,
        event: isUndefined(cnt) ? 'Close' : cnt === 0 ? 'Pause' : 'Limit',
        totalCnt: isUndefined(cnt) ? undefined : cnt,
        restoreType: 'everyday',
        startTime: `${ddayjs().add(1, 'day').format('YYYY-MM-DD')} 00:00:00`
      }
    }
    return params
  }
  /** 修改停售modify的数据吐出
   * 如果新的字端这里需要持续
   * type: saleOut(沽清) ｜ reStore(全部恢复) ｜ batchSaleOut(批量沽清) */
  changeModifyList (option: { modifier: IModifier[]; cnt?: number; type?: string; restoreType?: 'today' | 'long' | 'everyday' }) {
    const { modifier, cnt, type = 'saleOut', restoreType = 'today' } = option
    const modifierMap = keyBy(modifier, (i) => i.no)
    const newHandleList: IProductSaleHandle[] = cloneDeep(modifier.map(modify => {
      return this.converModify(modify, type, cnt, restoreType)
    }))
    this.handleList = unionBy(concat(this.handleList, newHandleList), 'productNo')
    let modifySort = cloneDeep(this.handleModifySort)
    let maxSort = isEmpty(modifySort) ? 0 : Math.max(...Object.values(modifySort).map(i => i.sort))
    this.handleList.forEach((item) => {
      const groupNo = modifierMap[item.productNo]?.groupNo
      if (modifySort[groupNo]) {
        if (item.event === 'Close') {
          const modifier = omit(modifySort[groupNo].modifier, [`${item.productNo}_0`])
          isEmpty(modifier) ? (modifySort = omit(modifySort, [groupNo])) : (modifySort[groupNo].modifier = modifier)
        } else {
          let modifyMax = isEmpty(modifySort[groupNo].modifier) ? 0 : Math.max(...Object.values(modifySort[groupNo].modifier))
          set(modifySort[groupNo].modifier, `${item.productNo}_0`, ++modifyMax)
        }
        modifySort[groupNo] && modifySort[groupNo].sort !== maxSort && set(modifySort[groupNo], 'sort', ++maxSort)
      } else {
        set(modifySort, groupNo, { sort: ++maxSort, modifier: { [`${item.productNo}_0`]: 1 } })
      }
    })
    this.setModifySort(modifySort)
    setProductSale(toJS(this.handleList))
  }
  /** 修改停售餐品 */
  changeDataList (data: IProductSaleHandle[]) {
    const groupData = groupBy(data, 'productNo')
    const groupList = groupBy(this.handleList, 'productNo')
    for (let key in groupData) {
      groupList[key] = groupData[key]
      const isToRemove = groupData[key].length === 1 && groupData[key][0].scaleNo === '-1' && groupData[key][0].cnt === undefined
      if (!isToRemove) {
        if (!this.handleSort.includes(key)) {
          this.handleSort.unshift(key)
        } else {
          const _idx = this.handleSort.indexOf(key)
          if (_idx > -1) {
            this.handleSort.splice(_idx, 1)
            this.handleSort.unshift(key)
          }
        }
      }
      // 如果餐品有且只有一条数据为全规格恢复则移除this.handleSort.productNo
      if (this.handleSort.includes(key) && isToRemove) {
        this.handleSort = this.handleSort.filter(v => v !== key)
      }
    }

    const newList: any[] = []
    for (let key in groupList) {
      groupList[key].map((v) => {
        newList.push(v)
      })
    }
    this.handleList = newList
    const convertProduct = (product: any) => {
      let paras: any = {
        ...product,
        productName: allMenuMobx.noToViewProductMap[product.productNo]?.name || '',
        scaleName: allMenuMobx.noToViewProductScaleMap[product.productNo + '_' + product.scaleNo]?.scaleName || '',
        restoreType: null,
        startTime: null
      }
      if (product?.restoreType === 'today') {
        // 仅今日沽清
        paras = {
          ...paras,
          restoreType: 'today',
          startTime: `${ddayjs().add(1, 'day').format('YYYY-MM-DD')} 00:00:00`
        }
      }
      if (product?.restoreType === 'everyday') {
        // 每天自动限售
        paras = {
          ...paras,
          restoreType: 'everyday',
          startTime: `${ddayjs().add(1, 'day').format('YYYY-MM-DD')} 00:00:00`
        }
      }
      if (product?.restoreType === 'long') {
        paras = {
          ...paras,
          restoreType: null,
          startTime: null
        }
      }
      return paras
    }
    const saveList = newList.map(v => {
      return convertProduct(v)
    })
    saveSaleList(saveList)
  }
}

export default new ProductSale()
